import { ArticleIcon, CaretRightIcon, CheckIcon, WarningIcon, XIcon } from "@phosphor-icons/react";
import { type CSSProperties, type ReactNode, useEffect, useId, useRef, useState } from "react";
import { number } from "../format.ts";
import { CHECK_LABELS, KIT_LABELS } from "../labels.ts";
import { AgentLogo, Avatar, agentEngine } from "./props.tsx";

// Reviewing what an agent submitted (docs/design-system.md §0.4d): server
// checks, agent-written text set apart, the history of an object, and the
// full-page review shaped like a pull-request review. Summary first (§0.3e):
// the facts a decision turns on are always shown; long text shows its first
// lines and opens in place; secondary detail folds. "Viewed" is only the
// person's own mark.

export type CheckStatus = keyof typeof CHECK_LABELS;

const CHECK_ICON: Record<CheckStatus, ReactNode> = {
  pass: <CheckIcon size={12} weight="bold" />,
  attention: <WarningIcon size={12} weight="bold" />,
  fail: <XIcon size={12} weight="bold" />,
};
const CHECK_TONE: Record<CheckStatus, string> = { pass: "green", attention: "amber", fail: "red" };

export interface CheckItem { status: CheckStatus; label: string; detail?: ReactNode }

function CheckRow({ item }: { item: CheckItem }) {
  return <>
    <span className="z-check-badge" data-tone={CHECK_TONE[item.status]} role="img" aria-label={CHECK_LABELS[item.status]} title={CHECK_LABELS[item.status]}>{CHECK_ICON[item.status]}</span>
    <span className="z-check-name">{item.label}</span>
    {item.detail && <span className="z-check-detail">{item.detail}</span>}
  </>;
}

/**
 * What the server checked: an icon and a word per line, and why. Unchecked
 * items are never shown. With `foldPassed`, what needs attention or failed
 * stays open and the passed checks fold into one line that opens in place.
 */
export function CheckList({ items, foldPassed = false }: { items: CheckItem[]; foldPassed?: boolean }) {
  const passed = foldPassed ? items.filter((item) => item.status === "pass") : [];
  const open = foldPassed ? items.filter((item) => item.status !== "pass") : items;
  return <ul className="z-checks">
    {open.map((item, index) => <li key={`${item.label}-${index}`}><CheckRow item={item} /></li>)}
    {passed.length > 0 && <li className="z-checks-passed"><details>
      <summary>
        <span className="z-check-badge" data-tone="green" aria-hidden>{CHECK_ICON.pass}</span>
        <span className="z-check-name">{open.length ? KIT_LABELS.passedChecks(passed.length) : KIT_LABELS.allPassed(passed.length)}</span>
        <CaretRightIcon className="z-more-caret" size={14} weight="bold" aria-hidden />
      </summary>
      <ul className="z-checks is-nested">{passed.map((item, index) => <li key={`${item.label}-${index}`}><CheckRow item={item} /></li>)}</ul>
    </details></li>}
  </ul>;
}

/**
 * Long text shown by its first `lines` (§0.3e), most often an agent's: the
 * rest opens in place with "Show more". The whole text always stays in the
 * page, for copying, assistive technology and the agent's context; text that
 * fits shows no control.
 */
export function Excerpt({ lines = 3, className, children }: { lines?: number; className?: string; children: ReactNode }) {
  const id = useId();
  const body = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [long, setLong] = useState(false);
  useEffect(() => {
    const element = body.current;
    if (!element || open) return;
    const measure = () => setLong(element.scrollHeight - element.clientHeight > 1);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [open, children, lines]);
  return <div className={className ? `z-excerpt ${className}` : "z-excerpt"}>
    <div ref={body} id={id} className="z-excerpt-body" data-clamped={open ? undefined : ""} data-long={long ? "" : undefined} style={{ "--z-clamp": lines } as CSSProperties}>{children}</div>
    {(long || open) && <button type="button" className="z-link z-excerpt-toggle" aria-expanded={open} aria-controls={id} onClick={() => setOpen(!open)}>{open ? KIT_LABELS.showLess : KIT_LABELS.showMore}</button>}
  </div>;
}

/** A short list, such as acceptance criteria: open while it has `lines` items or fewer, else an `Excerpt` of its first lines. */
export function PointList({ items, lines = 3 }: { items: string[]; lines?: number }) {
  const list = <ul className="z-points">{items.map((line, index) => <li key={index}>{line}</li>)}</ul>;
  return items.length > lines ? <Excerpt lines={lines}>{list}</Excerpt> : list;
}

/**
 * Text an agent wrote: content, kept as written (line breaks, no markup),
 * labelled until a person confirms it. `engine` draws the agent's logo;
 * `lines` shows only the first lines until the person opens the rest.
 */
export function AgentText({ name = "Claude Code", engine, label, lines, children }: { name?: string; engine?: string | null; label?: string; lines?: number; children: ReactNode }) {
  const logo = agentEngine(engine);
  return <div className="z-agent">
    <div className="z-agent-label">{logo ? <AgentLogo engine={logo} size={14} /> : <Avatar kind="agent" size={16} initials={name.split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase()} />}<span>{label ?? KIT_LABELS.agentWrote(name)}</span></div>
    <div className="z-agent-text">{lines ? <Excerpt lines={lines}>{children}</Excerpt> : children}</div>
  </div>;
}

export interface GlanceItem { label: string; value: ReactNode; note?: ReactNode }

/**
 * The few facts a decision turns on, read in one look before any text
 * (§0.3e): a label and a short value per tile. A value that could not be read
 * shows as "–".
 */
export function Glance({ items, size = "md", label = KIT_LABELS.keyFacts }: { items: GlanceItem[]; size?: "sm" | "md"; label?: string }) {
  return <dl className={`z-glance is-${size}`} aria-label={label}>{items.map((item) => <div key={item.label} className="z-glance-item">
    <dt>{item.label}</dt>
    <dd>{item.value ?? "–"}{item.note && <span className="z-glance-note">{item.note}</span>}</dd>
  </div>)}</dl>;
}

/**
 * Detail a decision does not turn on, folded under one line (§0.3e): what it
 * rests on, references, the reasoning behind a choice. A native details
 * element, so Enter and Space open it and find-in-page reaches it.
 */
export function More({ label = KIT_LABELS.moreDetails, count, children }: { label?: ReactNode; count?: number; children: ReactNode }) {
  return <details className="z-more">
    <summary><CaretRightIcon className="z-more-caret" size={14} weight="bold" aria-hidden /><span>{label}</span>{count !== undefined && <span className="z-more-count">{number(count)}</span>}</summary>
    <div className="z-more-body">{children}</div>
  </details>;
}

export interface ActivityItem {
  /** Who it was; ledger events without a person show an icon instead. */
  actor?: "agent" | "person";
  /** The agent's declared engine, for its logo. */
  engine?: string | null;
  icon?: ReactNode;
  text: ReactNode;
  at?: string;
}

/** An object's history, oldest first: one sentence per event, external content only as links. */
export function Activity({ items }: { items: ActivityItem[] }) {
  return <ol className="z-act">{items.map((item, index) => <li key={index}>
    <span className="z-act-lead" aria-hidden>{item.icon ? <span className="z-act-icon">{item.icon}</span> : <ActivityLead item={item} />}</span>
    <span className="z-act-text">{item.text}</span>
    {item.at && <time className="z-act-at">{item.at}</time>}
  </li>)}</ol>;
}

function ActivityLead({ item }: { item: ActivityItem }) {
  const logo = item.actor === "person" ? undefined : agentEngine(item.engine);
  return logo ? <AgentLogo engine={logo} size={18} /> : <Avatar kind={item.actor ?? "agent"} size={18} />;
}

export interface OutlineItem { id: string; label: string; icon?: ReactNode; level?: 1 | 2; viewed?: boolean; group?: boolean }

/** The left outline of a review: sections and cards, with the person's "viewed" marks. */
export function ReviewOutline({ items, active, onNavigate }: { items: OutlineItem[]; active?: string; onNavigate?: (id: string) => void }) {
  return <nav className="z-outline" aria-label={KIT_LABELS.outline}>{items.map((item) => item.group
    ? <div key={item.id} className="z-outline-group">{item.label}</div>
    : <a key={item.id} href={`#${item.id}`} onClick={() => onNavigate?.(item.id)} className={`z-outline-item${item.level === 2 ? " is-sub" : ""}`} aria-current={active === item.id ? "location" : undefined}>
      <span className="z-outline-icon" aria-hidden>{item.icon ?? <ArticleIcon size={16} />}</span>
      <span className="z-outline-label">{item.label}</span>
      {item.viewed && <span className="z-outline-viewed" title={KIT_LABELS.viewed}><CheckIcon size={14} weight="bold" aria-hidden /><span className="z-sr">{KIT_LABELS.viewed}</span></span>}
    </a>)}</nav>;
}

/** One card of a review, like a changed file in a pull request: the facts it turns on open, with a "viewed" box. */
export function ReviewCard({ id, kicker, title, viewed, onViewedChange, children }: { id: string; kicker?: ReactNode; title: ReactNode; viewed?: boolean; onViewedChange?: (viewed: boolean) => void; children: ReactNode }) {
  return <section className="z-rcard" id={id} aria-labelledby={`${id}-title`}>
    <header className="z-rcard-head">
      {kicker && <span className="z-rcard-kicker">{kicker}</span>}
      <h3 id={`${id}-title`}>{title}</h3>
      {onViewedChange && <label className="z-rcard-viewed"><input type="checkbox" checked={Boolean(viewed)} onChange={(event) => onViewedChange(event.target.checked)} />{KIT_LABELS.viewed}</label>}
    </header>
    <div className="z-rcard-body">{children}</div>
  </section>;
}

/** The bar fixed at the bottom of a review: where it stands, how much was viewed, and the decision. */
export function ReviewBar({ status, viewed, total, children }: { status?: ReactNode; viewed?: number; total?: number; children: ReactNode }) {
  return <div className="z-reviewbar" role="region" aria-label={KIT_LABELS.decision}>
    {status && <span className="z-reviewbar-status">{status}</span>}
    {total !== undefined && <span className="z-reviewbar-viewed">{KIT_LABELS.viewedOf(viewed ?? 0, total)}</span>}
    <span className="z-reviewbar-actions">{children}</span>
  </div>;
}
